/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  atualizarDepartamento,
  atualizarGrupo,
  atualizarVinculoBot,
  criarDepartamento,
  criarGrupo,
  excluirDepartamento,
  excluirGrupo,
  getResumo,
  inativarBot,
  listarDepartamentos,
  listarGrupos,
  listarVinculosBots,
  reativarBot,
  IVinculoBot,
  STALE_TEMPO_REAL,
} from '../api';
import { FONTE_MONO, FONTE_TEXTO } from '../paleta';
import { SelectFiltro, OpcaoSelect } from '../components/SelectFiltro';
import { MultiSelectGrupos } from '../components/MultiSelectGrupos';
import { empresasDentroDasRegras } from '../utils/derivarResumo';
import { regimesDisponiveis } from '../utils/filtrosCompartilhados';
import { herancaRegimeDoBot } from '../utils/regimeEfetivo';
import { useApelidos } from '../useApelidos';

/**
 * Aba DEPENDÊNCIAS — porta o template `isDependencias` do protótipo
 * (`sistema/index.html` linhas 536-713) 1:1: três cards empilhados (Dependências
 * entre bots / Grupos de bots / Departamentos), cada um com formulário de criação,
 * caixa de erro ⚠ e tabela com selects/edição inline (✎, ✓/✕, Enter confirma,
 * Escape cancela).
 *
 * Persistência REAL via CRUD `/orquestrador/dependencias/*` (config ao vivo, sem
 * cache): as mutações de constantes do protótipo não migram. Regras que o backend
 * garante e a UI só comunica: exclusão nunca apaga bot/grupo (só desfaz vínculo) e
 * ciclo de dependência → 400 com a mensagem exibida como veio. As validações de UX
 * (nome vazio/duplicado) continuam client-side, como no protótipo (C2 §3.12).
 *
 * Evolução de 04/09 sobre o template: a tabela de bots ganhou (a) coluna APELIDO
 * editável inline (bot_configs.apelido — só exibição, o nome real nunca muda; vazio
 * limpa) e (b) o select único de Grupo virou MULTI-SELECT (`grupo_ids`: bot em N
 * grupos), no estilo visual do protótipo — ver `MultiSelectGrupos`.
 *
 * Evolução de 08/09: SOFT-DELETE de bot (bots fantasmas do auto-cadastro legado) —
 * botão discreto "Remover" com confirmação INLINE na linha (nunca window.confirm) e
 * seção colapsada "Bots removidos (N)" com Restaurar por bot. Bot inativado some de
 * TODAS as leituras (o backend filtra na fonte; o client de `api.ts` refiltra por
 * `inativado_em` defensivamente).
 *
 * Evolução de 08/09 (regime por nível): coluna "Regime" nos 3 cards — regime
 * tributário definível por departamento, grupo e bot, com HERANÇA departamento →
 * grupo → bot (o mais específico vence; "—" = null = herdar). O regime efetivo age
 * como FILTRO das empresas exibidas nas visões (Processos/Resumo); as opções são
 * os regimes distintos que o /resumo entrega (mesmos labels do filtro global).
 */

const mensagemDoErro = (error: any, padrao: string): string => {
  const detail = error?.response?.data?.detail;
  if (!detail) return padrao;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return padrao;
};

/** Caixa de erro ⚠ dos formulários (linhas 545-549 do protótipo). */
const CaixaErro: React.FC<{ mensagem: string | null; compacta?: boolean }> = ({
  mensagem,
  compacta = true,
}) => {
  if (!mensagem) return null;
  return (
    <div
      style={{
        border: '1px solid oklch(0.85 0.08 28)',
        borderRadius: '8px',
        background: 'oklch(0.97 0.02 28)',
        padding: compacta ? '9px 12px' : '10px 14px',
        fontSize: compacta ? '12px' : '12.5px',
        color: 'oklch(0.4 0.1 28)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        ...(compacta ? {} : { margin: '0 22px 14px' }),
      }}
    >
      <span>⚠</span>
      {mensagem}
    </div>
  );
};

const inputFormStyle: React.CSSProperties = {
  flex: '1 1 220px',
  padding: '8px 12px',
  border: '1px solid oklch(0.9 0.006 160)',
  borderRadius: '8px',
  fontFamily: FONTE_TEXTO,
  fontSize: '12.5px',
  color: 'oklch(0.3 0.02 165)',
};

const botaoAdicionarStyle: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: '8px',
  background: 'oklch(0.22 0.026 168)',
  color: 'oklch(0.96 0.01 160)',
  fontSize: '12.5px',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const cardStyle: React.CSSProperties = {
  background: 'oklch(1 0 0)',
  border: '1px solid oklch(0.92 0.006 160)',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
};

const headerColunasStyle = (gridTemplateColumns: string): React.CSSProperties => ({
  padding: '0 22px 10px',
  display: 'grid',
  gridTemplateColumns,
  gap: '14px',
  fontFamily: FONTE_MONO,
  fontSize: '10px',
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'oklch(0.6 0.012 165)',
  borderBottom: '1px solid oklch(0.9 0.008 160)',
});

const linhaStyle = (gridTemplateColumns: string): React.CSSProperties => ({
  padding: '13px 22px',
  display: 'grid',
  gridTemplateColumns,
  gap: '14px',
  alignItems: 'center',
  borderBottom: '1px solid oklch(0.96 0.006 160)',
});

/** Nome editável inline (✎ abre input com ✓/✕; Enter confirma, Escape cancela). */
const NomeEditavel: React.FC<{
  nome: string;
  titleEditar?: string;
  // eslint-disable-next-line no-unused-vars
  onConfirmar: (novoNome: string) => void;
}> = ({ nome, titleEditar = 'Clique para renomear', onConfirmar }) => {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(nome);
  if (!editando) {
    return (
      <div
        onClick={() => {
          setValor(nome);
          setEditando(true);
        }}
        title={titleEditar}
        style={{
          fontSize: '13.5px',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        {nome}
        <span style={{ fontSize: '10px', color: 'oklch(0.65 0.012 165)' }}>✎</span>
      </div>
    );
  }
  const confirmar = () => {
    // Como no protótipo (confirmarEdicaoGrupo): nome vazio mostra o erro do card
    // (via onConfirmar) e PERMANECE em modo edição — o input só fecha com nome válido.
    onConfirmar(valor);
    if (!valor.trim()) return;
    setEditando(false);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <input
        type="text"
        value={valor}
        onChange={ev => setValor(ev.target.value)}
        onKeyDown={ev => {
          if (ev.key === 'Enter') confirmar();
          else if (ev.key === 'Escape') setEditando(false);
        }}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        style={{
          flex: 1,
          minWidth: 0,
          padding: '6px 10px',
          border: '1px solid oklch(0.45 0.1 165)',
          borderRadius: '7px',
          fontFamily: FONTE_TEXTO,
          fontSize: '13px',
          fontWeight: 600,
          color: 'oklch(0.3 0.02 165)',
        }}
      />
      <span
        onClick={confirmar}
        title="Salvar"
        style={{ flex: 'none', cursor: 'pointer', color: 'oklch(0.4 0.1 165)', fontSize: '13px' }}
      >
        ✓
      </span>
      <span
        onClick={() => setEditando(false)}
        title="Cancelar"
        style={{
          flex: 'none',
          cursor: 'pointer',
          color: 'oklch(0.55 0.015 165)',
          fontSize: '13px',
        }}
      >
        ✕
      </span>
    </div>
  );
};

/**
 * Apelido de exibição editável inline — mesmo padrão do renomear grupo (✎ abre input
 * com ✓/✕; Enter confirma, Escape cancela), com UMA diferença deliberada: apelido é
 * OPCIONAL, então confirmar VAZIO não é erro — limpa o apelido (PATCH `apelido: null`)
 * e a tela volta a mostrar o nome real do bot em todo lugar.
 */
const ApelidoEditavel: React.FC<{
  apelido: string | null;
  /** Nome real do bot — só para o title do estado sem apelido. */
  bot: string;
  // eslint-disable-next-line no-unused-vars
  onConfirmar: (novoApelido: string | null) => void;
}> = ({ apelido, bot, onConfirmar }) => {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(apelido ?? '');
  if (!editando) {
    return (
      <div
        onClick={() => {
          setValor(apelido ?? '');
          setEditando(true);
        }}
        title={
          apelido
            ? `Apelido de exibição de ${bot} — clique para editar (vazio limpa)`
            : `Definir apelido de exibição para ${bot} (o nome real não muda)`
        }
        style={{
          fontSize: '13.5px',
          fontWeight: apelido ? 600 : 400,
          color: apelido ? 'oklch(0.3 0.02 165)' : 'oklch(0.65 0.012 165)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          minWidth: 0,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {apelido ?? '—'}
        </span>
        <span style={{ fontSize: '10px', color: 'oklch(0.65 0.012 165)', flex: 'none' }}>✎</span>
      </div>
    );
  }
  const confirmar = () => {
    onConfirmar(valor.trim() || null);
    setEditando(false);
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <input
        type="text"
        value={valor}
        placeholder="Sem apelido"
        onChange={ev => setValor(ev.target.value)}
        onKeyDown={ev => {
          if (ev.key === 'Enter') confirmar();
          else if (ev.key === 'Escape') setEditando(false);
        }}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        style={{
          flex: 1,
          minWidth: 0,
          padding: '6px 10px',
          border: '1px solid oklch(0.45 0.1 165)',
          borderRadius: '7px',
          fontFamily: FONTE_TEXTO,
          fontSize: '13px',
          fontWeight: 600,
          color: 'oklch(0.3 0.02 165)',
        }}
      />
      <span
        onClick={confirmar}
        title="Salvar (vazio limpa o apelido)"
        style={{ flex: 'none', cursor: 'pointer', color: 'oklch(0.4 0.1 165)', fontSize: '13px' }}
      >
        ✓
      </span>
      <span
        onClick={() => setEditando(false)}
        title="Cancelar"
        style={{
          flex: 'none',
          cursor: 'pointer',
          color: 'oklch(0.55 0.015 165)',
          fontSize: '13px',
        }}
      >
        ✕
      </span>
    </div>
  );
};

/**
 * Select compacto da coluna "Regime" (regime por nível, 08/09) — mesmo padrão
 * visual dos selects de tabela do card. "—" = null = herdar do nível acima; com
 * valor null e herança presente na cadeia, mostra abaixo "herda: {regime}" em
 * cinza itálico (no card de bots, heranças divergentes → "herda: varia por grupo").
 * Valor salvo que não está mais no dataset do /resumo continua selecionável e
 * limpável, mas a option o DENUNCIA com o sufixo "(fora do dataset)" (correção
 * M3 da revisão 08/09: label órfão idêntico aos demais parecia opção válida).
 *
 * Exportado para o teste de render do sufixo órfão — só a aba usa em produção.
 */
export const SelectRegime: React.FC<{
  valor: string | null;
  opcoes: string[];
  /** Texto de herança já resolvido pelo chamador — null = sem hint. */
  herdado: string | null;
  // eslint-disable-next-line no-unused-vars
  onChange: (novo: string | null) => void;
}> = ({ valor, opcoes, herdado, onChange }) => {
  const orfao = valor && !opcoes.includes(valor) ? valor : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
      <SelectFiltro
        value={valor ?? ''}
        onChange={novo => onChange(novo || null)}
        opcoes={[
          { value: '', label: '—' },
          ...opcoes.map(r => ({ value: r, label: r })),
          ...(orfao ? [{ value: orfao, label: `${orfao} (fora do dataset)` }] : []),
        ]}
        selectStyle={{ width: '100%', padding: '7px 30px 7px 12px' }}
      />
      {valor === null && herdado && (
        <div
          title={`Sem regime próprio neste nível — herda "${herdado}" do nível acima`}
          style={{
            fontSize: '10.5px',
            fontStyle: 'italic',
            color: 'oklch(0.62 0.012 165)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          herda: {herdado}
        </div>
      )}
    </div>
  );
};

export const DependenciasTab: React.FC = () => {
  const queryClient = useQueryClient();
  const { apelidoDoBot, temApelido } = useApelidos();

  const { data: grupos } = useQuery({ queryKey: ['orqDepGrupos'], queryFn: () => listarGrupos() });
  const { data: departamentos } = useQuery({
    queryKey: ['orqDepDepartamentos'],
    queryFn: () => listarDepartamentos(),
  });
  const { data: vinculos } = useQuery({
    queryKey: ['orqVinculosBots'],
    queryFn: () => listarVinculosBots(),
  });
  // Mesma queryKey BASE com o param (contrato 08/09) — o invalidar() por prefixo
  // (['orqVinculosBots']) atualiza as duas listas de uma vez.
  const { data: vinculosComInativados } = useQuery({
    queryKey: ['orqVinculosBots', { incluirInativados: true }],
    queryFn: () => listarVinculosBots(true),
  });
  const botsRemovidos = (vinculosComInativados ?? []).filter(v => v.inativado_em);

  // Opções do select de regime por nível: os regimes DISTINTOS que o /resumo
  // entrega (labels normalizados do BFF) — mesma derivação do filtro global de
  // regime, sobre o dataset completo (queryKey compartilhada com o Resumo).
  const { data: resumoTotal } = useQuery({
    queryKey: ['orqResumoTotal'],
    queryFn: () => getResumo({}),
    staleTime: STALE_TEMPO_REAL,
  });
  const opcoesRegime = useMemo(
    () => regimesDisponiveis(empresasDentroDasRegras(resumoTotal?.empresas ?? [])),
    [resumoTotal],
  );
  // Mapas para resolver a herança exibida ("herda: ..."): grupo → departamento.
  const grupoPorId = useMemo(() => new Map((grupos ?? []).map(g => [g.id, g])), [grupos]);
  const departamentoPorId = useMemo(
    () => new Map((departamentos ?? []).map(d => [d.id, d])),
    [departamentos],
  );
  /** Hint de herança do BOT: única entre os grupos, "varia por grupo", ou nada. */
  const herancaHintDoBot = (v: IVinculoBot): string | null => {
    const heranca = herancaRegimeDoBot(v.grupo_ids, grupoPorId, departamentoPorId);
    if (heranca.tipo === 'varia') return 'varia por grupo';
    return heranca.tipo === 'unica' ? heranca.regime : null;
  };

  // Erro por CARD — a mensagem aparece perto de onde a ação falhou, como no protótipo.
  const [cadBotErro, setCadBotErro] = useState<string | null>(null);
  const [depsErro, setDepsErro] = useState<string | null>(null);
  const [cadGrupoErro, setCadGrupoErro] = useState<string | null>(null);
  const [cadDepartamentoErro, setCadDepartamentoErro] = useState<string | null>(null);

  const [cadBotNome, setCadBotNome] = useState('');
  const [cadBotDependeDe, setCadBotDependeDe] = useState('');
  const [cadGrupoNome, setCadGrupoNome] = useState('');
  const [cadGrupoDependeDe, setCadGrupoDependeDe] = useState('');
  const [cadDepartamentoNome, setCadDepartamentoNome] = useState('');

  // Soft-delete de bot (pedido 08/09): confirmação INLINE na própria linha (nunca
  // window.confirm — CI trata o warning como erro) + seção colapsada de removidos.
  const [confirmandoRemocao, setConfirmandoRemocao] = useState<string | null>(null);
  const [removidosAberto, setRemovidosAberto] = useState(false);

  /** Toda mutação invalida config E boards agregados — a esteira lê esses vínculos. */
  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ['orqDepGrupos'] });
    queryClient.invalidateQueries({ queryKey: ['orqDepDepartamentos'] });
    queryClient.invalidateQueries({ queryKey: ['orqVinculosBots'] });
    queryClient.invalidateQueries({ queryKey: ['orqEsteira'] });
    queryClient.invalidateQueries({ queryKey: ['orqGrupoBoard'] });
  };

  const executar = async (
    acao: () => Promise<unknown>,
    setErro: React.Dispatch<React.SetStateAction<string | null>>,
  ): Promise<boolean> => {
    setErro(null);
    try {
      await acao();
      invalidar();
      return true;
    } catch (error) {
      setErro(mensagemDoErro(error, 'Não foi possível salvar a alteração.'));
      return false;
    }
  };

  // "+ Nova automação": o contrato NÃO tem POST de bot — o catálogo é alimentado pelo
  // próprio bot via SDK de logging. O formulário do protótipo permanece; submeter
  // explica isso honestamente em vez de fingir um cadastro.
  const onCadBotSubmit = () => {
    if (!cadBotNome.trim()) {
      setCadBotErro('Informe o nome da automação.');
      return;
    }
    setCadBotErro(
      'Cadastro de automação ainda não disponível por aqui: o catálogo de bots é alimentado pelo próprio bot, via SDK de logging, na primeira execução. Use esta tabela para definir os vínculos de um bot já existente.',
    );
  };

  const onCadGrupoSubmit = () => {
    const nome = cadGrupoNome.trim();
    if (!nome) {
      setCadGrupoErro('Informe o nome do grupo.');
      return;
    }
    if ((grupos ?? []).some(g => g.nome.toLowerCase() === nome.toLowerCase())) {
      setCadGrupoErro('Já existe um grupo com este nome.');
      return;
    }
    // Grupo novo nasce SEM departamento — o usuário associa depois pelo select.
    executar(
      () => criarGrupo({ nome, depende_de: cadGrupoDependeDe || null }),
      setCadGrupoErro,
    ).then(ok => {
      if (!ok) return;
      setCadGrupoNome('');
      setCadGrupoDependeDe('');
    });
  };

  const onCadDepartamentoSubmit = () => {
    const nome = cadDepartamentoNome.trim();
    if (!nome) {
      setCadDepartamentoErro('Informe o nome do departamento.');
      return;
    }
    if ((departamentos ?? []).some(d => d.nome.toLowerCase() === nome.toLowerCase())) {
      setCadDepartamentoErro('Já existe um departamento com este nome.');
      return;
    }
    executar(() => criarDepartamento({ nome }), setCadDepartamentoErro).then(ok => {
      if (ok) setCadDepartamentoNome('');
    });
  };

  // Opções exibem o APELIDO (fallback nome real); o value é SEMPRE o nome real.
  const opcoesBotDependeDe = (botAtual: string): OpcaoSelect[] => [
    { value: '', label: 'Nenhum' },
    ...(vinculos ?? [])
      .filter(v => v.bot !== botAtual)
      .map(v => ({ value: v.bot, label: apelidoDoBot(v.bot) })),
  ];

  /**
   * Toggle de um grupo no multi-select: monta a LISTA COMPLETA nova e PATCHa
   * `grupo_ids` inteiro (contrato — o backend substitui a lista, não faz diff).
   *
   * Sem corrida em toggles rápidos: a lista nova parte do CACHE mais recente da
   * queryKey (não do `v` capturado no render) e a mutação aplica optimistic
   * update nessa mesma queryKey — o segundo toggle já enxerga o primeiro antes
   * do refetch. O onError devolve o snapshot (rollback) e mostra o erro no card.
   */
  const grupoIdsMutation = useMutation({
    mutationFn: ({ bot, grupoIds }: { bot: string; grupoIds: string[] }) =>
      atualizarVinculoBot(bot, { grupo_ids: grupoIds }),
    onMutate: async ({ bot, grupoIds }) => {
      setDepsErro(null);
      await queryClient.cancelQueries({ queryKey: ['orqVinculosBots'] });
      const anterior = queryClient.getQueryData<IVinculoBot[]>(['orqVinculosBots']);
      queryClient.setQueryData<IVinculoBot[]>(['orqVinculosBots'], atual =>
        atual?.map(item => (item.bot === bot ? { ...item, grupo_ids: grupoIds } : item)),
      );
      return { anterior };
    },
    onError: (error, _variaveis, contexto) => {
      if (contexto?.anterior) {
        queryClient.setQueryData(['orqVinculosBots'], contexto.anterior);
      }
      setDepsErro(mensagemDoErro(error, 'Não foi possível salvar a alteração.'));
    },
    onSuccess: () => invalidar(),
  });

  const alternarGrupo = (v: IVinculoBot, grupoId: string) => {
    // Estado MAIS RECENTE no clique: o cache (que já carrega o optimistic update
    // do toggle anterior), não o `v` do render — senão o toggle de B montado
    // sobre a lista sem A faria o PATCH de B apagar A em silêncio.
    const atual =
      queryClient
        .getQueryData<IVinculoBot[]>(['orqVinculosBots'])
        ?.find(item => item.bot === v.bot) ?? v;
    const novaLista = atual.grupo_ids.includes(grupoId)
      ? atual.grupo_ids.filter(id => id !== grupoId)
      : [...atual.grupo_ids, grupoId];
    grupoIdsMutation.mutate({ bot: v.bot, grupoIds: novaLista });
  };

  /**
   * Soft-delete/restauração de bot — mesmo padrão useMutation da tela: sucesso
   * invalida os vínculos (prefixo `orqVinculosBots` cobre a lista principal E a de
   * removidos) e o erro cai na CaixaErro do card (o 404 de backend ainda não
   * publicado aparece ali, sem crash).
   */
  const inativacaoMutation = useMutation({
    mutationFn: ({ bot, acao }: { bot: string; acao: 'inativar' | 'reativar' }) =>
      acao === 'inativar' ? inativarBot(bot) : reativarBot(bot),
    onMutate: () => setDepsErro(null),
    onSuccess: () => invalidar(),
    onError: (error, { acao }) =>
      setDepsErro(
        mensagemDoErro(
          error,
          acao === 'inativar'
            ? 'Não foi possível remover o bot.'
            : 'Não foi possível restaurar o bot.',
        ),
      ),
    onSettled: () => setConfirmandoRemocao(null),
  });

  const formatarDataRemocao = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `removido em ${d.toLocaleDateString('pt-BR')}`;
  };

  const opcoesDepartamentoComNenhum: OpcaoSelect[] = [
    { value: '', label: 'Nenhum' },
    ...(departamentos ?? []).map(d => ({ value: d.id, label: d.nome })),
  ];

  const selectTabelaStyle: React.CSSProperties = { width: '100%', padding: '7px 30px 7px 12px' };
  const selectVerdeStyle: React.CSSProperties = {
    ...selectTabelaStyle,
    border: '1px solid oklch(0.85 0.06 145)',
    background: 'oklch(0.96 0.025 145)',
    color: 'oklch(0.35 0.09 145)',
  };

  const gridBots =
    'minmax(0, 0.85fr) minmax(0, 0.7fr) minmax(0, 0.8fr) minmax(0, 1fr) minmax(0, 0.75fr) minmax(0, 0.35fr)';
  const gridRemovidos = 'minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 0.5fr)';
  const gridGrupos =
    'minmax(0, 0.95fr) minmax(0, 0.8fr) minmax(0, 0.8fr) minmax(0, 0.8fr) minmax(0, 0.45fr) minmax(0, 0.5fr)';
  const gridDepartamentos = 'minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 0.7fr) minmax(0, 0.5fr)';

  return (
    <>
      {/* ── Card 1: Dependências entre bots ─────────────────────────────────── */}
      <div style={cardStyle}>
        <div
          style={{
            padding: '20px 22px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '3px',
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600 }}>Dependências entre bots</div>
          <div style={{ fontSize: '12.5px', color: 'oklch(0.55 0.015 165)' }}>
            Para cada bot, de qual outro ele precisa terminar antes de começar.
          </div>
        </div>

        <div
          style={{
            margin: '0 22px 18px',
            padding: '16px',
            border: '1px solid oklch(0.9 0.006 160)',
            borderRadius: '10px',
            background: 'oklch(0.985 0.006 160)',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600 }}>+ Nova automação</div>
          <CaixaErro mensagem={cadBotErro} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Nome da automação"
              value={cadBotNome}
              onChange={ev => {
                setCadBotNome(ev.target.value);
                setCadBotErro(null);
              }}
              style={inputFormStyle}
            />
            <SelectFiltro
              value={cadBotDependeDe}
              onChange={setCadBotDependeDe}
              opcoes={[
                { value: '', label: 'Nenhuma' },
                ...(vinculos ?? []).map(v => ({ value: v.bot, label: apelidoDoBot(v.bot) })),
              ]}
              wrapperStyle={{ flex: '1 1 220px' }}
              selectStyle={{
                width: '100%',
                padding: '8px 30px 8px 12px',
                fontWeight: 400,
              }}
            />
            <div onClick={onCadBotSubmit} style={botaoAdicionarStyle}>
              Adicionar
            </div>
          </div>
        </div>

        {depsErro && <CaixaErro mensagem={depsErro} compacta={false} />}

        <div style={headerColunasStyle(gridBots)}>
          <div>Bot</div>
          <div>Apelido</div>
          <div>Depende de</div>
          <div>Grupos</div>
          <div>Regime</div>
          <div />
        </div>
        {(vinculos ?? []).map(v => (
          <div key={v.bot} style={linhaStyle(gridBots)}>
            <div
              title={temApelido(v.bot) ? `Nome real: ${v.bot}` : undefined}
              style={{
                fontSize: '13.5px',
                fontWeight: 600,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {v.bot}
            </div>
            <ApelidoEditavel
              apelido={v.apelido}
              bot={v.bot}
              onConfirmar={novoApelido =>
                executar(() => atualizarVinculoBot(v.bot, { apelido: novoApelido }), setDepsErro)
              }
            />
            <SelectFiltro
              value={v.dep_bot ?? ''}
              onChange={novo =>
                executar(() => atualizarVinculoBot(v.bot, { dep_bot: novo || null }), setDepsErro)
              }
              opcoes={opcoesBotDependeDe(v.bot)}
              selectStyle={selectTabelaStyle}
            />
            <MultiSelectGrupos
              selecionados={v.grupo_ids}
              opcoes={(grupos ?? []).map(g => ({ id: g.id, nome: g.nome }))}
              onToggle={grupoId => alternarGrupo(v, grupoId)}
            />
            <SelectRegime
              valor={v.regime ?? null}
              opcoes={opcoesRegime}
              herdado={herancaHintDoBot(v)}
              onChange={novo =>
                executar(() => atualizarVinculoBot(v.bot, { regime: novo }), setDepsErro)
              }
            />
            {confirmandoRemocao === v.bot ? (
              // Confirmação INLINE — mesmo vocabulário ✓/✕ da edição de nome.
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: '8px',
                }}
              >
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'oklch(0.5 0.16 28)' }}>
                  Remover?
                </span>
                <span
                  onClick={() => inativacaoMutation.mutate({ bot: v.bot, acao: 'inativar' })}
                  title="Confirmar remoção (o histórico não é apagado — dá para restaurar)"
                  style={{
                    flex: 'none',
                    cursor: 'pointer',
                    color: 'oklch(0.55 0.13 28)',
                    fontSize: '13px',
                    fontWeight: 700,
                  }}
                >
                  ✓
                </span>
                <span
                  onClick={() => setConfirmandoRemocao(null)}
                  title="Cancelar"
                  style={{
                    flex: 'none',
                    cursor: 'pointer',
                    color: 'oklch(0.55 0.015 165)',
                    fontSize: '13px',
                  }}
                >
                  ✕
                </span>
              </div>
            ) : (
              <div
                onClick={() => setConfirmandoRemocao(v.bot)}
                title="Remover bot desta tela (soft-delete — o histórico não é apagado e dá para restaurar)"
                style={{
                  cursor: 'pointer',
                  color: 'oklch(0.55 0.13 28)',
                  fontSize: '12px',
                  textAlign: 'right',
                }}
              >
                Remover
              </div>
            )}
          </div>
        ))}
        {(vinculos ?? []).length === 0 && (
          <div
            style={{
              padding: '18px 22px',
              fontSize: '12.5px',
              color: 'oklch(0.55 0.015 165)',
            }}
          >
            Nenhum bot no catálogo ainda — o catálogo é alimentado pelos próprios bots via SDK de
            logging.
          </div>
        )}

        {/* Bots removidos (soft-delete) — seção COLAPSADA ao final da seção de bots. */}
        {botsRemovidos.length > 0 && (
          <>
            <div
              onClick={() => setRemovidosAberto(aberto => !aberto)}
              title={
                removidosAberto ? 'Recolher bots removidos' : 'Ver os bots removidos desta tela'
              }
              style={{
                padding: '12px 22px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderTop: '1px solid oklch(0.96 0.006 160)',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '14px',
                  height: '14px',
                  flex: 'none',
                  color: 'oklch(0.55 0.015 165)',
                  fontSize: '9px',
                  transform: removidosAberto ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s',
                }}
              >
                ▶
              </span>
              <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'oklch(0.5 0.015 165)' }}>
                Bots removidos ({botsRemovidos.length})
              </span>
            </div>
            {removidosAberto &&
              botsRemovidos.map(v => (
                <div
                  key={v.bot}
                  style={{
                    padding: '11px 22px',
                    display: 'grid',
                    gridTemplateColumns: gridRemovidos,
                    gap: '14px',
                    alignItems: 'center',
                    borderTop: '1px solid oklch(0.97 0.004 160)',
                  }}
                >
                  <div
                    title={temApelido(v.bot) ? `Nome real: ${v.bot}` : undefined}
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'oklch(0.55 0.015 165)',
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {v.bot}
                  </div>
                  <div
                    style={{
                      fontFamily: FONTE_MONO,
                      fontSize: '11px',
                      color: 'oklch(0.6 0.012 165)',
                    }}
                  >
                    {formatarDataRemocao(v.inativado_em)}
                  </div>
                  <div
                    onClick={() => inativacaoMutation.mutate({ bot: v.bot, acao: 'reativar' })}
                    title="Restaurar bot (volta a aparecer em todas as listas)"
                    style={{
                      cursor: 'pointer',
                      color: 'oklch(0.4 0.1 165)',
                      fontSize: '12px',
                      fontWeight: 600,
                      textAlign: 'right',
                    }}
                  >
                    Restaurar
                  </div>
                </div>
              ))}
          </>
        )}
      </div>

      {/* ── Card 2: Grupos de bots ──────────────────────────────────────────── */}
      <div style={cardStyle}>
        <div
          style={{
            padding: '20px 22px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '3px',
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600 }}>Grupos de bots</div>
          <div style={{ fontSize: '12.5px', color: 'oklch(0.55 0.015 165)' }}>
            Agrupamento simples e visual — sem regra de negócio, você define. Excluir um grupo não
            apaga os bots, só desfaz o vínculo.
          </div>
        </div>

        <div
          style={{
            margin: '0 22px 18px',
            padding: '16px',
            border: '1px solid oklch(0.9 0.006 160)',
            borderRadius: '10px',
            background: 'oklch(0.985 0.006 160)',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600 }}>+ Novo grupo</div>
          <CaixaErro mensagem={cadGrupoErro} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Nome do grupo"
              value={cadGrupoNome}
              onChange={ev => {
                setCadGrupoNome(ev.target.value);
                setCadGrupoErro(null);
              }}
              style={inputFormStyle}
            />
            <SelectFiltro
              value={cadGrupoDependeDe}
              onChange={setCadGrupoDependeDe}
              opcoes={[
                { value: '', label: 'Nenhum' },
                ...(grupos ?? []).map(g => ({ value: g.id, label: g.nome })),
              ]}
              wrapperStyle={{ flex: '1 1 220px' }}
              selectStyle={{ width: '100%', padding: '8px 30px 8px 12px', fontWeight: 400 }}
            />
            <div onClick={onCadGrupoSubmit} style={botaoAdicionarStyle}>
              Adicionar
            </div>
          </div>
        </div>

        <div style={headerColunasStyle(gridGrupos)}>
          <div>Grupo</div>
          <div>Depende de</div>
          <div>Departamento</div>
          <div>Regime</div>
          <div>Bots</div>
          <div />
        </div>
        {(grupos ?? []).map(g => {
          // Bot em N grupos conta em CADA um deles (grupo_ids é lista, não campo único).
          const qtdBots = (vinculos ?? []).filter(v => v.grupo_ids.includes(g.id)).length;
          return (
            <div key={g.id} style={linhaStyle(gridGrupos)}>
              <NomeEditavel
                nome={g.nome}
                onConfirmar={novoNome => {
                  const nome = novoNome.trim();
                  if (!nome) {
                    setCadGrupoErro('Informe o nome do grupo.');
                    return;
                  }
                  executar(() => atualizarGrupo(g.id, { nome }), setCadGrupoErro);
                }}
              />
              <SelectFiltro
                value={g.depende_de ?? ''}
                onChange={novo =>
                  executar(
                    () => atualizarGrupo(g.id, { depende_de: novo || null }),
                    setCadGrupoErro,
                  )
                }
                opcoes={[
                  { value: '', label: 'Nenhum' },
                  ...(grupos ?? [])
                    .filter(outro => outro.id !== g.id)
                    .map(outro => ({ value: outro.id, label: outro.nome })),
                ]}
                selectStyle={selectTabelaStyle}
              />
              <SelectFiltro
                value={g.departamento_id ?? ''}
                onChange={novo =>
                  executar(
                    () => atualizarGrupo(g.id, { departamento_id: novo || null }),
                    setCadGrupoErro,
                  )
                }
                opcoes={opcoesDepartamentoComNenhum}
                selectStyle={selectVerdeStyle}
                chevronStyle={{ color: 'oklch(0.42 0.09 145)' }}
              />
              <SelectRegime
                valor={g.regime ?? null}
                opcoes={opcoesRegime}
                herdado={
                  g.departamento_id
                    ? (departamentoPorId.get(g.departamento_id)?.regime ?? null)
                    : null
                }
                onChange={novo =>
                  executar(() => atualizarGrupo(g.id, { regime: novo }), setCadGrupoErro)
                }
              />
              <div
                style={{
                  fontFamily: FONTE_MONO,
                  fontSize: '11.5px',
                  color: 'oklch(0.58 0.015 165)',
                }}
              >
                {qtdBots === 1 ? '1 bot' : `${qtdBots} bots`}
              </div>
              <div
                onClick={() => executar(() => excluirGrupo(g.id), setCadGrupoErro)}
                title="Excluir grupo (bots ficam sem grupo)"
                style={{
                  cursor: 'pointer',
                  color: 'oklch(0.55 0.13 28)',
                  fontSize: '12px',
                  textAlign: 'right',
                }}
              >
                Excluir
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Card 3: Departamentos ───────────────────────────────────────────── */}
      <div style={cardStyle}>
        <div
          style={{
            padding: '20px 22px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '3px',
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600 }}>Departamentos</div>
          <div style={{ fontSize: '12.5px', color: 'oklch(0.55 0.015 165)' }}>
            Área organizacional de cada grupo (DP, Contábil, Fiscal...) — só um separador visual da
            futura tela macro. Excluir um departamento não apaga grupos, só desfaz o vínculo.
          </div>
        </div>

        <div
          style={{
            margin: '0 22px 18px',
            padding: '16px',
            border: '1px solid oklch(0.9 0.006 160)',
            borderRadius: '10px',
            background: 'oklch(0.985 0.006 160)',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600 }}>+ Novo departamento</div>
          <CaixaErro mensagem={cadDepartamentoErro} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Nome do departamento"
              value={cadDepartamentoNome}
              onChange={ev => {
                setCadDepartamentoNome(ev.target.value);
                setCadDepartamentoErro(null);
              }}
              style={inputFormStyle}
            />
            <div onClick={onCadDepartamentoSubmit} style={botaoAdicionarStyle}>
              Adicionar
            </div>
          </div>
        </div>

        <div style={headerColunasStyle(gridDepartamentos)}>
          <div>Departamento</div>
          <div>Regime</div>
          <div>Grupos</div>
          <div />
        </div>
        {(departamentos ?? []).map(d => {
          const qtdGrupos = (grupos ?? []).filter(g => g.departamento_id === d.id).length;
          return (
            <div key={d.id} style={linhaStyle(gridDepartamentos)}>
              <NomeEditavel
                nome={d.nome}
                onConfirmar={novoNome => {
                  const nome = novoNome.trim();
                  if (!nome) {
                    setCadDepartamentoErro('Informe o nome do departamento.');
                    return;
                  }
                  executar(() => atualizarDepartamento(d.id, { nome }), setCadDepartamentoErro);
                }}
              />
              {/* Departamento é o TOPO da herança — nunca mostra "herda:". */}
              <SelectRegime
                valor={d.regime ?? null}
                opcoes={opcoesRegime}
                herdado={null}
                onChange={novo =>
                  executar(
                    () => atualizarDepartamento(d.id, { regime: novo }),
                    setCadDepartamentoErro,
                  )
                }
              />
              <div
                style={{
                  fontFamily: FONTE_MONO,
                  fontSize: '11.5px',
                  color: 'oklch(0.58 0.015 165)',
                }}
              >
                {qtdGrupos === 1 ? '1 grupo' : `${qtdGrupos} grupos`}
              </div>
              <div
                onClick={() => executar(() => excluirDepartamento(d.id), setCadDepartamentoErro)}
                title="Excluir departamento (grupos ficam sem departamento)"
                style={{
                  cursor: 'pointer',
                  color: 'oklch(0.55 0.13 28)',
                  fontSize: '12px',
                  textAlign: 'right',
                }}
              >
                Excluir
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};
